import {observer} from 'mobx-react-lite'
import React from 'react'
import {getColumnColorHex} from 'yougile-model'
import {DeadlineStatus} from '@components/aggregated/types'
import {IconButtonNew} from '@components/common/base/buttons'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {BoxDragTarget} from '@components/timeline-new/box-helper'
import {BackgroundSwimlane} from '@components/timeline-new/diagram/graph/background-swimlane'
import {TaskBoxLink} from '@components/timeline-new/diagram/graph/task-box-link'
import {
  rowHeight,
  useTimelineNewStore
} from '@components/timeline-new/timeline-new-store'
import {LinkData, RowData} from '@components/timeline-new/types'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {DeadlineCtl} from '@extensions/sticker/controllers/deadline-ctl'
import {
  TwContainer,
  TwContainerBox,
  TwLeftHandle,
  TwLinkHandle,
  TwRightHandle,
  TwSwimlanes,
  TwTaskBox,
  TwTaskBoxContainer,
  TwTaskBoxPlaceholder
} from './containers'
import {DragLinkLine} from './drag-link-line'
import {EmptySwimlane} from './empty-swimlane'
import {IntervalStripes, WeekendStripes} from './vertical-stripes'

// Swimlanes component is larger than a table, add more lanes here
const virtualizeThreshold = 5

const createLinkFilter = (fromRow: number, toRow: number) => (x: LinkData) => {
  if (fromRow <= x.fromSwimlane && x.fromSwimlane <= toRow) {
    return true
  } else if (fromRow <= x.toSwimlane && x.toSwimlane <= toRow) {
    return true
  } else if (x.fromSwimlane <= fromRow && toRow <= x.toSwimlane) {
    return true
  } else if (x.toSwimlane <= fromRow && toRow <= x.fromSwimlane) {
    return true
  }
  return false
}

export interface SwimlanesProps {
  className?: string
}

export const Swimlanes = observer((props: SwimlanesProps) => {
  const {className} = props
  const [state, store] = useTimelineNewStore()

  const filterFn = createLinkFilter(state.fromRow, state.toRow)
  const displayedLinks = state.links.filter(filterFn)

  return (
    <TwSwimlanes ref={store.setSwimlanesEl} className={className}>
      <TwContainer ref={store.setSwimlanesContainer}>
        <WeekendStripes className="z-50" />
        <IntervalStripes />

        {state.tableRows.map((x, i) => {
          const top = i * rowHeight
          const path = x.data.path

          const key = `${path}__${i}`

          if (
            i < state.fromRow - virtualizeThreshold ||
            i > state.toRow + virtualizeThreshold
          ) {
            return <EmptySwimlane key={key} ix={i} path={path} />
          }

          if (!(x.data.hubId in state.itemDataRegistry)) {
            return <EmptySwimlane key={key} ix={i} path={path} />
          }

          const {deadline} = state.itemDataRegistry[x.data.hubId]
          if (!deadline.start || !deadline.end) {
            return <EmptySwimlane key={key} ix={i} path={path} />
          }

          return (
            <React.Fragment key={key}>
              <BackgroundSwimlane ix={i} path={path} />
              <div
                className="h-40 absolute w-full z-20 pointer-events-none"
                style={{transform: `translate(0px,${top}px)`}}
              >
                <Swimlane
                  key={key}
                  data={x.data}
                  deadline={deadline}
                  swimlaneIx={i}
                />
              </div>
            </React.Fragment>
          )
        })}
        {displayedLinks.map((data, i) => (
          <TaskBoxLink
            key={`${data.fromPath}-${data.toPath}__${i}`}
            data={data}
          />
        ))}
        {state.linkingPath && <DragLinkLine />}
      </TwContainer>
    </TwSwimlanes>
  )
})

interface SwimlaneProps {
  className?: string
  data: RowData
  deadline: DeadlineStatus
  swimlaneIx: number
}

const Swimlane = observer((props: SwimlaneProps) => {
  const [state, store] = useTimelineNewStore()

  if (!props.deadline.start || !props.deadline.end) {
    return null
  }

  const data = props.data

  const blockedRangeSections = props.data.hub
    ? DeadlineCtl.getBlockedPoints(props.data.hub)
    : []
  const canDragOnlyEnd = blockedRangeSections.includes('start')
  const isCompleted = props.deadline.status === 'completed'
  const isContainer = data.type === 'column' || data.type === 'board'
  const isSubtask = data.isSubtask
  const isExpanded = store.getExpanded({type: data.type, path: data.path})
  const isDragging = !!state.draggingPath
  const isDraggingSelf = isDragging && data.path === state.draggingPath
  const isDraggingOther = isDragging && !isDraggingSelf
  const isLinking = !!state.linkingPath
  const isLinkingSelf = isLinking && state.linkingPath === data.path
  const draggingStart = isDraggingSelf ? state.draggingStart : undefined
  const draggingEnd = isDraggingSelf ? state.draggingEnd : undefined

  let start = draggingStart ?? props.deadline.start
  let end = draggingEnd ?? props.deadline.end
  // Limit large bars to display window width to prevent issues w/ multiyear tasks
  if (state.leftBoundary !== Number.POSITIVE_INFINITY) {
    start = Math.max(start, state.leftBoundary)
  }
  if (state.rightBoundary !== Number.NEGATIVE_INFINITY) {
    end = Math.min(end, state.rightBoundary)
  }
  const snappedStart = store.startTimeToSnappedTime(start)
  const snappedEnd = store.endTimeToSnappedTime(end)

  let placeholderStart: number | undefined = undefined
  let placeholderEnd: number | undefined = undefined
  let dragTarget: BoxDragTarget | undefined = undefined
  if (isDraggingSelf && canDragOnlyEnd) {
    placeholderStart = store.startTimeToSnappedTime(props.deadline.start)
    placeholderEnd = store.endTimeToSnappedTime(props.deadline.end)
    dragTarget = state.draggingTarget
  }

  return (
    <SwimlaneBase
      {...props}
      start={snappedStart}
      end={snappedEnd}
      placeholderStart={placeholderStart}
      placeholderEnd={placeholderEnd}
      isCompleted={isCompleted}
      isContainer={isContainer}
      isSubtask={isSubtask}
      isExpanded={isExpanded}
      isLinking={isLinking}
      isLinkingSelf={isLinkingSelf}
      isDraggingSelf={isDraggingSelf}
      isDraggingOther={isDraggingOther}
      canDragStart={!canDragOnlyEnd}
      canDragBoth={!canDragOnlyEnd}
      dragTarget={dragTarget}
    />
  )
})

interface SwimlaneBaseProps extends SwimlaneProps {
  start: number
  end: number
  placeholderStart?: number
  placeholderEnd?: number
  canDragStart?: boolean
  canDragEnd?: boolean
  canDragBoth?: boolean
  isCompleted?: boolean
  isContainer?: boolean
  isSubtask?: boolean
  isExpanded?: boolean
  isLinking?: boolean
  isLinkingSelf?: boolean
  isDraggingSelf?: boolean
  isDraggingOther?: boolean
  dragTarget?: BoxDragTarget
}

const SwimlaneBase = observer((props: SwimlaneBaseProps) => {
  const {data, swimlaneIx} = props
  const {start, end, isDraggingSelf, isDraggingOther} = props
  const {isExpanded, isLinking, isLinkingSelf} = props
  const {isCompleted, isContainer, isSubtask} = props
  const {canDragStart = true, canDragBoth = true, canDragEnd = true} = props
  const {placeholderStart, placeholderEnd, dragTarget} = props
  const [, store] = useTimelineNewStore()

  if (!start && !end) {
    return null
  }

  const left = store.timeToPosition(start)
  const width = store.intervalToDistance(end - start)
  const color = getColumnColorHex({hue: data.hue}, globals.theme)

  return isContainer ? (
    <TwContainerBox
      style={{
        transform: `translate(${left}px,23px)`,
        width,
        backgroundColor: color,
        color
      }}
      onMouseMove={() => store.setHoveredRow(data.path)}
      onMouseLeave={() => store.setHoveredRow(undefined)}
    />
  ) : (
    <>
      {placeholderStart && placeholderEnd && dragTarget === 'box' && (
        <TwTaskBoxPlaceholder
          style={{
            transform: `translate(${store.timeToPosition(placeholderStart)}px,${
              isSubtask ? 12 : 8
            }px)`,
            width: store.intervalToDistance(placeholderEnd - placeholderStart),
            height: isSubtask ? 16 : 24
          }}
        />
      )}
      <TwTaskBoxContainer
        $subtask={isSubtask}
        $draggable={data.draggable && canDragBoth}
        $dragging={isDraggingSelf}
        $linking={isLinking}
        $linkingSelf={isLinkingSelf}
        $hoverable={
          !isLinking && !isDraggingOther && (data.draggable || data.linkable)
        }
        style={{
          transform: `translate(${left}px,${isSubtask ? 12 : 8}px)`,
          width
        }}
        onMouseDown={e => {
          e.stopPropagation()
          data.draggable &&
            store.boxHelper.startBoxDrag('box', data.path, data.hubId, e)
        }}
        onMouseUp={e => store.linkHelper.boxMouseUp(data.hubId, e)}
        onMouseMove={() => store.setHoveredRow(data.path)}
        onMouseLeave={() => store.setHoveredRow(undefined)}
      >
        <TwTaskBox
          $completed={isCompleted}
          $canDragStart={canDragStart}
          $canDragEnd={canDragEnd}
          $draggable={data.draggable}
          $dragging={isDraggingSelf}
          $linking={isLinking}
          $hoverable={
            !isLinking && !isDraggingOther && (data.draggable || data.linkable)
          }
          style={{backgroundColor: isCompleted ? undefined : color}}
        />
        {isCompleted && (
          <IconTabler
            icon="IconCustomTaskDoneFilledSmall"
            viewBox="0 0 24 24"
            customSize={16}
            className={cn(
              'flex-none',
              'block',
              'pointer-events-none',
              'z-10',
              width < 20 ? 'absolute -right-[14px]' : 'ml-4'
            )}
          />
        )}
        {data.expandable && (
          <IconButtonNew
            icon={isExpanded ? 'IconChevronUp' : 'IconChevronDown'}
            className="absolute -left-[20px]"
            onClick={() => store.toggleExpanded(data.path, data.type)}
          />
        )}
        {data.draggable && canDragStart && (
          <TwLeftHandle
            $visible={isDraggingSelf}
            $hidden={isLinking || isDraggingOther}
            $interactable={!isDraggingSelf}
            onMouseDown={e =>
              store.boxHelper.startBoxDrag('left', data.path, data.hubId, e)
            }
          />
        )}
        {data.draggable && canDragEnd && (
          <TwRightHandle
            $visible={isDraggingSelf}
            $hidden={isLinking || isDraggingOther}
            $interactable={!isDraggingSelf && !isLinking}
            onMouseDown={e =>
              store.boxHelper.startBoxDrag('right', data.path, data.hubId, e)
            }
          />
        )}
        {data.linkable && (
          <TwLinkHandle
            $hidden={isDraggingSelf || isDraggingOther || isLinking}
            $interactable={!isDraggingSelf}
            onMouseDown={e =>
              store.linkHelper.startBoxLink(
                data.path,
                data.hubId,
                swimlaneIx,
                e
              )
            }
          />
        )}
      </TwTaskBoxContainer>
    </>
  )
})
